import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface MyProperty {
  id: string;
  title: string;
  type: string;
  purpose: string;
  price: number | string;
  city: string;
  status: string;
  coverImage: string | null;
  createdAt: string;
}

const STATUS: Record<string, { label: string; color: 'green' | 'amber' | 'navy' | 'red' | 'gray' }> = {
  ACTIVE:         { label: 'منشور',         color: 'green' },
  PENDING_REVIEW: { label: 'قيد المراجعة',   color: 'amber' },
  DRAFT:          { label: 'مسودة',          color: 'gray' },
  SOLD:           { label: 'تم البيع',       color: 'navy' },
  RENTED:         { label: 'تم التأجير',     color: 'navy' },
  REJECTED:       { label: 'مرفوض',          color: 'red' },
};

export function MyPropertiesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['me', 'properties'],
    queryFn: () => api.get<ApiSuccess<MyProperty[]>>('/api/me/properties'),
  });

  const list = data?.data ?? [];

  return (
    <div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-navy-dark">🏘️ عقاراتي</h1>
          <p className="text-sm text-ink-muted mt-1">إدارة عقاراتك المنشورة والمعلّقة على المنصة</p>
        </div>
        <Link to="/submit-property">
          <Button variant="primary">➕ إضافة عقار جديد</Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="text-gray-500">⟳ جاري التحميل…</div>
      ) : list.length === 0 ? (
        <Card>
          <CardBody className="text-center py-16">
            <div className="text-6xl mb-3">🏘️</div>
            <p className="text-gray-600 mb-4">لم تضف أي عقار بعد.</p>
            <div className="flex gap-2 justify-center flex-wrap">
              <Link to="/submit-property">
                <Button variant="primary">➕ أضف عقارك الأول</Button>
              </Link>
              <Link to="/request-auction">
                <Button variant="gold">🔨 اطلب مزاداً</Button>
              </Link>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((p) => (
            <Link key={p.id} to={`/properties/${p.id}`}>
              <Card hover>
                <div className="h-40 bg-gradient-to-br from-navy to-navy-dark relative">
                  {p.coverImage ? (
                    <img src={p.coverImage} alt={p.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white text-5xl">🏠</div>
                  )}
                  <div className="absolute top-2 right-2">
                    <Badge color={STATUS[p.status]?.color ?? 'gray'}>{STATUS[p.status]?.label ?? p.status}</Badge>
                  </div>
                </div>
                <CardBody>
                  <h3 className="font-black text-ink line-clamp-1">{p.title}</h3>
                  <p className="text-xs text-gray-500 mb-2">📍 {p.city}</p>
                  <div className="font-black text-navy-dark">{Number(p.price).toLocaleString('en-US')} <span className="text-xs text-gray-500">ر.س</span></div>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
